import { LoadingSpinner as LoadingSpinnerPrime } from "components/LoadingSpinner";
import { ProgressSpinner } from "primereact/progressspinner";
import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "store";
import styled from "styled-components";

const RecommendationsContainer = styled.div`
  margin-top: 0.75rem;
  margin-bottom: 1rem;
  padding: 1rem;
  border: 1px solid var(--gray-300);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const RecommendedCptLink = styled.button<{ $isLoading?: boolean }>`
  background: none;
  border: none;
  color: var(--primary-500);
  text-decoration: underline;
  cursor: ${({ $isLoading }) => ($isLoading ? "not-allowed" : "pointer")};
  font-size: inherit;
  font-family: inherit;
  padding: 0;
  margin: 0;
  opacity: ${({ $isLoading }) => ($isLoading ? 0.6 : 1)};
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    color: ${({ $isLoading }) =>
      $isLoading ? "var(--primary-500)" : "var(--primary-600)"};
    text-decoration: ${({ $isLoading }) => ($isLoading ? "underline" : "none")};
  }

  &:focus {
    outline: 2px solid #007ad6;
    outline-offset: 1px;
    border-radius: 2px;
  }
`;

const RecommendedCptText = styled.span`
  color: var(--gray-700);
  font-weight: 500;
  font-size: inherit;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
`;

const LoadingSpinner = styled.span`
  display: inline-flex;
  margin-left: 0.25rem;
`;

interface ICptRecommendationsProps {
  onRecommendationClick: (cptCode: string) => void;
  loadingCptCode?: string | null;
}

export function CptRecommendations({
  onRecommendationClick,
  loadingCptCode,
}: ICptRecommendationsProps) {
  const { cptRecommendations, loadingCptRecommendations } = useSelector(
    (state: RootState) => state.serviceDetailsSlice
  );

  // Group recommendations by the CPT codes they're paired with
  const groupedRecommendations = cptRecommendations.reduce(
    (acc, recommendation) => {
      recommendation.pairedWith.forEach((pairedCode) => {
        if (!acc[pairedCode]) {
          acc[pairedCode] = [];
        }
        acc[pairedCode].push(recommendation);
      });
      return acc;
    },
    {} as Record<string, typeof cptRecommendations>
  );

  if (loadingCptRecommendations) {
    return (
      <LoadingSpinnerPrime customStyle={{ width: "16px", height: "16px" }} />
    );
  }

  if (!cptRecommendations || cptRecommendations.length === 0) {
    return null;
  }

  // If no grouped recommendations, don't show anything
  if (Object.keys(groupedRecommendations).length === 0) {
    return null;
  }

  return (
    <RecommendationsContainer>
      {Object.entries(groupedRecommendations).map(
        ([existingCode, recommendations]) => (
          <div className="text-sm font-normal" key={existingCode}>
            <span className="font-semibold">{existingCode}</span> commonly
            paired with:{" "}
            {recommendations.map((recommendation, index) => {
              const isLoading = loadingCptCode === recommendation.cptCode;
              const canClick = recommendation.existsForPayer && !isLoading;

              return (
                <span key={recommendation.cptCode}>
                  {index > 0 && ", "}
                  {canClick ? (
                    <RecommendedCptLink
                      $isLoading={isLoading}
                      onClick={() =>
                        onRecommendationClick(recommendation.cptCode)
                      }
                      disabled={isLoading}
                    >
                      {recommendation.cptCode}
                      {isLoading && (
                        <LoadingSpinner>
                          <ProgressSpinner
                            style={{ width: "12px", height: "12px" }}
                            strokeWidth="4"
                          />
                        </LoadingSpinner>
                      )}
                    </RecommendedCptLink>
                  ) : (
                    <RecommendedCptText>
                      {recommendation.cptCode}
                    </RecommendedCptText>
                  )}{" "}
                  <span className="text-gray-500">
                    ({recommendation.occurrenceRate}%)
                  </span>
                </span>
              );
            })}
          </div>
        )
      )}
    </RecommendationsContainer>
  );
}
